import React, { useEffect } from "react";
import { AlertTriangle, Braces } from "lucide-react";

/* ===================== STYLES ===================== */
import styles from "./ErrorHandling.module.scss";

const ErrorHandling = ({
  errors = [],
  onRowClick,
  selectedJson,
  isAdminView = false,
}) => {
  useEffect(() => {}, [selectedJson]);

  const renderJson = (data) => {
    if (!data) return "No error response available";

    if (typeof data === "string") {
      try {
        return JSON.stringify(JSON.parse(data), null, 2);
      } catch {
        return data;
      }
    }

    return JSON.stringify(data, null, 2);
  };

  const getStatusClass = (code) => {
    const status = Number(code);

    switch (status) {
      case 200:
      case 201:
        return styles["admin-err-handling__badge--success"];
      case 400:
      case 404:
      case 422:
        return styles["admin-err-handling__badge--warning"];
      case 500:
      case 502:
      case 503:
        return styles["admin-err-handling__badge--danger"];
      default:
        return styles["admin-err-handling__badge--default"];
    }
  };

  // Helper component to render a dark JSON block
  const CodeBlock = ({ title, data }) => (
    <div className={styles["admin-err-handling__code-block"]}>
      <div className={styles["admin-err-handling__code-header"]}>
        <Braces size={16} className={styles["admin-err-handling__code-icon"]} />
        <span>{title}</span>
      </div>
      <div className={styles["admin-err-handling__code-wrapper"]}>
        <pre className={styles["admin-err-handling__pre"]}>
          {renderJson(data)}
        </pre>
      </div>
    </div>
  );

  return (
    <div className={styles["admin-err-handling__card"]}>
      {/* HEADER */}
      <div className={styles["admin-err-handling__header"]}>
        <div className={styles["admin-err-handling__icon-wrapper"]}>
          <AlertTriangle size={18} strokeWidth={2.5} />
        </div>
        <h3 className={styles["admin-err-handling__title"]}>Error Handling</h3>
      </div>

      {/* TABLE */}
      <div className={styles["admin-err-handling__table-wrapper"]}>
        <table className={styles["admin-err-handling__table"]}>
          <thead>
            <tr>
              <th className={styles["admin-err-handling__th"]}>Title</th>
              <th className={styles["admin-err-handling__th"]}>Code</th>
              {isAdminView && (
                <th className={styles["admin-err-handling__th"]}>Vendor Key</th>
              )}
              {isAdminView && (
                <th className={styles["admin-err-handling__th"]}>Value</th>
              )}
              <th className={styles["admin-err-handling__th"]}>Description</th>
            </tr>
          </thead>

          <tbody>
            {errors.length > 0 ? (
              errors.map((item) => (
                <tr
                  key={item.id}
                  className={`${styles["admin-err-handling__tr"]} ${styles["admin-err-handling__tr--clickable"]}`}
                  onClick={() => onRowClick(item.id)}
                  title="Click to view JSON response"
                >
                  <td className={styles["admin-err-handling__td"]}>
                    {item.title || "--"}
                  </td>
                  <td className={styles["admin-err-handling__td"]}>
                    <span
                      className={`${styles["admin-err-handling__badge"]} ${getStatusClass(
                        item.name,
                      )}`}
                    >
                      {item.name || "--"}
                    </span>
                  </td>
                  {isAdminView && (
                    <td className={styles["admin-err-handling__td"]}>
                      {item.vendor_key || "--"}
                    </td>
                  )}
                  {isAdminView && (
                    <td className={styles["admin-err-handling__td"]}>
                      {item.value || "--"}
                    </td>
                  )}
                  <td className={styles["admin-req-header__td"]}>
                    {item.description || "--"}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={isAdminView ? 5 : 3}
                  className={styles["admin-err-handling__empty"]}
                >
                  No error details available
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* JSON SECTIONS */}
      <div className={styles["admin-err-handling__json-section"]}>
        {/* USER VIEW → ONLY RESPONSE */}
        {!isAdminView && (
          <CodeBlock title="Response" data={selectedJson?.response} />
        )}

        {/* ADMIN VIEW → ALL 3 */}
        {isAdminView && (
          <>
            <CodeBlock
              title="Vendor (JSON)"
              data={selectedJson?.responseVendor}
            />
            <CodeBlock title="Doql (JSON)" data={selectedJson?.response} />
            <CodeBlock
              title="Template (JSON)"
              data={selectedJson?.responseTemplate}
            />
          </>
        )}
      </div>
    </div>
  );
};

export default ErrorHandling;
